import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { publishersManageService } from '../../../api/publishersManageService';
import type { CreatePublisherManageDto } from '../../../types/managePublishers';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Tab = 'metadata' | 'drm';

const emptyDraft: CreatePublisherManageDto = {
  name: '',
  isActive: true,
  isDrmEnabled: false,
};

export default function PublisherCreateDrawer({ open, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('metadata');
  const [draft, setDraft] = useState<CreatePublisherManageDto>(emptyDraft);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset when the drawer opens so a previous draft doesn't bleed in.
  useEffect(() => {
    if (open) {
      setDraft(emptyDraft);
      setTab('metadata');
      setErrorMessage(null);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () => publishersManageService.create(draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manage-publishers'] });
      onClose();
    },
    onError: (err: any) => {
      const msg: string =
        err?.response?.data?.error?.message ??
        err?.message ??
        t('App.Admin.Publishers.PublisherCreateDrawer.FailedToCreate');
      setErrorMessage(msg);
    },
  });

  if (!open) return null;

  const canSave = draft.name.trim().length > 0 && !create.isPending;

  return (
    <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose}>
      <aside
        className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <h2 className="text-lg font-semibold">{t('App.Admin.Publishers.PublisherCreateDrawer.Title')}</h2>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-neutral-900" aria-label={t('App.Admin.Publishers.PublisherCreateDrawer.CloseAriaLabel')}>×</button>
        </header>

        <nav className="flex border-b border-neutral-200">
          {(['metadata', 'drm'] as const).map(t_ => (
            <button
              key={t_}
              type="button"
              onClick={() => setTab(t_)}
              className={`px-4 py-2 text-sm ${tab === t_ ? 'border-b-2 border-primary-600 text-primary-600' : 'text-neutral-600 hover:bg-neutral-50'}`}
            >
              {t_ === 'metadata' ? t('App.Admin.Publishers.PublisherCreateDrawer.TabMetadata') : t('App.Admin.Publishers.PublisherCreateDrawer.TabDrm')}
            </button>
          ))}
        </nav>

        {errorMessage && (
          <div className="bg-red-50 border-b border-red-200 text-red-800 text-sm px-4 py-2">
            {errorMessage}
          </div>
        )}

        <div className="flex-1 overflow-auto p-4">
          {tab === 'metadata' && (
            <div className="space-y-3">
              <Field label={t('App.Admin.Publishers.PublisherCreateDrawer.FieldName')}>
                <input
                  className={textInput}
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  autoFocus
                />
              </Field>
              <Field label={t('App.Admin.Publishers.PublisherCreateDrawer.FieldContactEmail')}>
                <input
                  className={textInput}
                  type="email"
                  value={draft.contactEmail ?? ''}
                  onChange={(e) => setDraft({ ...draft, contactEmail: e.target.value || undefined })}
                />
              </Field>
              <Field label={t('App.Admin.Publishers.PublisherCreateDrawer.FieldContactPhone')}>
                <input
                  className={textInput}
                  value={draft.contactPhone ?? ''}
                  onChange={(e) => setDraft({ ...draft, contactPhone: e.target.value || undefined })}
                />
              </Field>
              <Field label={t('App.Admin.Publishers.PublisherCreateDrawer.FieldWebsite')}>
                <input
                  className={textInput}
                  type="url"
                  value={draft.website ?? ''}
                  onChange={(e) => setDraft({ ...draft, website: e.target.value || undefined })}
                />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                />
                {t('App.Admin.Publishers.PublisherCreateDrawer.FieldActive')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.isDrmEnabled}
                  onChange={(e) => setDraft({ ...draft, isDrmEnabled: e.target.checked })}
                />
                {t('App.Admin.Publishers.PublisherCreateDrawer.FieldDrmEnabled')}
              </label>
            </div>
          )}

          {tab === 'drm' && (
            <div className="space-y-3">
              <p className="text-xs text-neutral-500">
                {t('App.Admin.Publishers.PublisherCreateDrawer.DrmTabHint')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <NumField label={t('App.Admin.Publishers.PublisherCreateDrawer.FieldPrintLimit')}         v={draft.defaultPrintQuotaLimit}         onChange={(v) => setDraft({ ...draft, defaultPrintQuotaLimit: v })} />
                <NumField label={t('App.Admin.Publishers.PublisherCreateDrawer.FieldPrintWindow')} v={draft.defaultPrintQuotaWindowDays}    onChange={(v) => setDraft({ ...draft, defaultPrintQuotaWindowDays: v })} />
                <NumField label={t('App.Admin.Publishers.PublisherCreateDrawer.FieldCopyLimit')}          v={draft.defaultCopyQuotaLimit}          onChange={(v) => setDraft({ ...draft, defaultCopyQuotaLimit: v })} />
                <NumField label={t('App.Admin.Publishers.PublisherCreateDrawer.FieldCopyWindow')}  v={draft.defaultCopyQuotaWindowDays}     onChange={(v) => setDraft({ ...draft, defaultCopyQuotaWindowDays: v })} />
                <NumField label={t('App.Admin.Publishers.PublisherCreateDrawer.FieldDownloadLimit')}      v={draft.defaultDownloadQuotaLimit}      onChange={(v) => setDraft({ ...draft, defaultDownloadQuotaLimit: v })} />
                <NumField label={t('App.Admin.Publishers.PublisherCreateDrawer.FieldDownloadWindow')} v={draft.defaultDownloadQuotaWindowDays} onChange={(v) => setDraft({ ...draft, defaultDownloadQuotaWindowDays: v })} />
              </div>
            </div>
          )}
        </div>

        <footer className="border-t border-neutral-200 px-4 py-3 flex justify-between items-center">
          <button type="button" onClick={onClose} className="text-sm text-neutral-600 hover:underline">{t('App.Admin.Publishers.PublisherCreateDrawer.Cancel')}</button>
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={!canSave}
            className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
          >
            {create.isPending ? t('App.Admin.Publishers.PublisherCreateDrawer.Saving') : t('App.Admin.Publishers.PublisherCreateDrawer.Create')}
          </button>
        </footer>
      </aside>
    </div>
  );
}

const textInput = 'border border-neutral-300 rounded px-2 py-1 text-sm w-full';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-neutral-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function NumField({ label, v, onChange }: { label: string; v?: number; onChange: (v?: number) => void }) {
  return (
    <label className="text-xs text-neutral-600">
      {label}
      <input
        type="number"
        min={0}
        value={v ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        className="border border-neutral-300 rounded px-2 py-1 text-sm w-full mt-1"
      />
    </label>
  );
}
